import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../../lib/useApi.js';
import { useUrlPagination } from '../../lib/usePagination.js';
import {
  appendWorkspaceReturnContext,
  buildWorkspaceReturnTarget,
  prependWorkspaceBreadcrumb,
} from '../../lib/workspaceContext.js';
import {
  buildEnhancementReportPath,
  buildExecutiveDeckPath,
} from '../briefings/reportEntryLinks.js';
import { buildStatusPackLibraryPath } from '../status/statusPackLinks.js';
import { RelatedEvidencePreview } from '../workspaces/RelatedEvidencePreview.jsx';
import {
  PageHeader,
  Breadcrumbs,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  HealthIndicator,
  PageViewerControls,
  KpiHeroBand,
  StatusDonutMini,
  InlineScoreCell,
  WorkflowPipeline,
  buildPipelineSteps,
  KpiBandSkeleton,
  TableSkeleton,
  BrandedEmptyState,
  StaggerReveal,
} from '../../components/index.js';
import { format } from '@dha-nexus/shared';
import './programs.redesign.css';

// ---- Program workspace tab consolidation (FS-P3M-2, AC-P3M-2) --------------
// The 10 legacy tabs collapse into 6 (mirroring the portfolio workspace).
// Thin/stub tab content merges into its related group as a stacked section,
// and legacy ?tab= values are normalized onto their new home tab so existing
// deep links keep working.
export const PROGRAM_WORKSPACE_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'money', label: 'Money' },
  { key: 'risks', label: 'Risks & Actions' },
  { key: 'decisions', label: 'Decisions & Roadmap' },
  { key: 'evidence', label: 'Evidence & AI' },
];

const PROGRAM_WORKSPACE_TAB_KEYS = new Set(PROGRAM_WORKSPACE_TABS.map((tab) => tab.key));

// Legacy 10-tab keys → new 6-tab home. Exported so tests can prove the
// old→new mapping stays total.
export const PROGRAM_WORKSPACE_LEGACY_TAB_MAP = {
  overview: 'overview',
  analytics: 'overview',
  planner: 'delivery',
  builder: 'delivery',
  capacity: 'delivery',
  roadmap: 'decisions',
  risks: 'risks',
  budget: 'money',
  ai: 'evidence',
  products: 'evidence',
};

// Canonical program lifecycle for the detail-page WorkflowPipeline ribbon.
const PROGRAM_LIFECYCLE_ORDER = ['intake', 'planning', 'execution', 'sustainment', 'closeout'];
const PROGRAM_LIFECYCLE_LABELS = {
  intake: 'Intake',
  planning: 'Planning',
  execution: 'Execution',
  sustainment: 'Sustainment',
  closeout: 'Closeout',
};

// Map a coarse health/status token to executive-traffic-light tone names.
function statusTone(value) {
  const v = String(value || '').toLowerCase();
  if (
    [
      'red',
      'atrisk',
      'at-risk',
      'at_risk',
      'critical',
      'blocked',
      'offtrack',
      'off-track',
    ].includes(v)
  )
    return 'brick';
  if (['amber', 'yellow', 'watch', 'caution', 'warning'].includes(v)) return 'amber';
  if (['green', 'ontrack', 'on-track', 'on_track', 'healthy', 'good', 'complete'].includes(v))
    return 'sage';
  return 'slate';
}

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Infer a lifecycle phase for the pipeline from program status/health signals
// using only already-fetched data. Defaults to execution for active programs.
function inferProgramLifecyclePhase(program, kpis) {
  const status = String(program?.status || program?.health?.overall || '').toLowerCase();
  if (['intake', 'proposed', 'new'].some((k) => status.includes(k))) return 'intake';
  if (['plan', 'planning', 'mobilize'].some((k) => status.includes(k))) return 'planning';
  if (['sustain', 'operate', 'steady'].some((k) => status.includes(k))) return 'sustainment';
  if (['closeout', 'closed', 'complete', 'retired'].some((k) => status.includes(k)))
    return 'closeout';
  if (toNum(kpis?.activeProjects) > 0) return 'execution';
  return 'planning';
}

function ConfiguredFieldHighlights({ highlights = [] }) {
  if (!Array.isArray(highlights) || highlights.length === 0) return null;
  const maskedHighlights = highlights.filter((highlight) => highlight?.masked);
  return (
    <div className="muted" style={{ marginTop: 8 }}>
      {highlights.map((highlight) => (
        <div key={highlight.fieldKey}>
          {highlight.label}: {highlight.displayValue}
        </div>
      ))}
      {maskedHighlights.length > 0 ? (
        <div>
          {maskedHighlights[0].message ||
            'Sensitive configured field data is masked for your role.'}
        </div>
      ) : null}
    </div>
  );
}

function formatFinancialSummaryAmountUsdM(value) {
  return format.formatCurrencyMillions(Number(value || 0) * 1_000_000);
}

function calculateBurnRatePercent(financials = {}) {
  if (financials?.burnRatePercent != null) return Number(financials.burnRatePercent);
  const plannedBudgetUsdM = Number(financials?.plannedBudgetUsdM || 0);
  const actualSpendUsdM = Number(financials?.actualSpendUsdM || 0);
  return plannedBudgetUsdM > 0 ? Math.round((actualSpendUsdM / plannedBudgetUsdM) * 100) : 0;
}

function formatFreshnessLabel(value) {
  return String(value || 'unknown')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function getProgramWorkspaceTabFromSearchParams(searchParams) {
  const requestedTab = searchParams.get('tab');
  if (PROGRAM_WORKSPACE_TAB_KEYS.has(requestedTab)) return requestedTab;
  return PROGRAM_WORKSPACE_LEGACY_TAB_MAP[requestedTab] || 'overview';
}

export function buildProgramWorkspaceTabSearchParams(searchParams, nextTab) {
  const resolved = PROGRAM_WORKSPACE_TAB_KEYS.has(nextTab)
    ? nextTab
    : PROGRAM_WORKSPACE_LEGACY_TAB_MAP[nextTab] || 'overview';
  const next = new URLSearchParams(searchParams);
  if (resolved === 'overview') {
    next.delete('tab');
  } else {
    next.set('tab', resolved);
  }
  return next;
}

export function ProgramListPage() {
  const [params, setParams] = useSearchParams();
  const pager = useUrlPagination(params, setParams, { defaultPageSize: 10 });
  const r = useApi('/programs', { query: { limit: pager.pageSize, offset: pager.offset } });

  if (r.loading)
    return (
      <div className="pgm-redesign">
        <PageHeader
          eyebrow="Executive"
          title="Programs"
          subtitle="Active programs across portfolios."
        />
        <KpiBandSkeleton count={4} />
        <div style={{ marginTop: 16 }}>
          <TableSkeleton rows={6} />
        </div>
      </div>
    );
  if (r.error) return <ErrorState error={r.error} />;

  const items = r.data?.data || [];

  if (!items.length)
    return (
      <div className="pgm-redesign">
        <PageHeader
          eyebrow="Executive"
          title="Programs"
          subtitle="Active programs across portfolios."
        />
        <BrandedEmptyState
          icon="🛰️"
          eyebrow="Program register"
          title="No programs"
          description="Active programs across portfolios will appear here once they are registered in DHA PRIME."
          tone="slate"
        />
      </div>
    );

  // Health distribution across the loaded page powers both the hero ring and a
  // headline count. Built only from data already present on each list record.
  const dist = items.reduce(
    (acc, p) => {
      const tone = statusTone(p.health?.overall || p.status);
      acc[tone] = (acc[tone] || 0) + 1;
      return acc;
    },
    { sage: 0, amber: 0, brick: 0, slate: 0 },
  );
  const attention = dist.brick + dist.amber;
  const heroItems = [
    {
      id: 'programs',
      label: 'Programs',
      value: items.length,
      footer: `${r.data?.page?.total ?? items.length} total in register`,
    },
    {
      id: 'on-track',
      label: 'On track',
      value: dist.sage,
      status: 'sage',
      statusLabel: 'Healthy',
    },
    {
      id: 'attention',
      label: 'Need attention',
      value: attention,
      status: dist.brick > 0 ? 'brick' : attention > 0 ? 'amber' : 'sage',
      statusLabel: dist.brick > 0 ? 'At risk' : attention > 0 ? 'Watch' : 'Clear',
    },
    {
      id: 'unrated',
      label: 'Unrated health',
      value: dist.slate,
      footer: dist.slate > 0 ? 'Awaiting status signal' : 'All rated',
    },
  ];

  const healthSegments = [
    { label: 'On track', count: dist.sage, tone: 'sage' },
    { label: 'Watch', count: dist.amber, tone: 'amber' },
    { label: 'At risk', count: dist.brick, tone: 'brick' },
    { label: 'Unrated', count: dist.slate, tone: 'slate' },
  ];

  return (
    <div className="pgm-redesign">
      <PageHeader
        eyebrow="Executive"
        title="Programs"
        subtitle="Active programs across portfolios."
      />

      <KpiHeroBand eyebrow="Program posture" items={heroItems} columns={4} />

      <section className="bento" style={{ marginTop: 16 }}>
        <div className="span-4 card pgm-health-card">
          <h2 className="card-title">Health mix</h2>
          <StatusDonutMini
            segments={healthSegments}
            size={104}
            totalLabel="Programs"
            centerValue={items.length}
            ariaLabel="Program health distribution"
          />
        </div>

        <StaggerReveal className="span-8 pgm-grid">
          {items.map((p) => {
            const tone = statusTone(p.health?.overall || p.status);
            return (
              <Link to={`/programs/${p.id}`} key={p.id} className="card interactive pgm-card">
                <div className="pgm-card__head">
                  <div className="pgm-card__title">{p.name}</div>
                  <HealthIndicator health={p.health?.overall || p.status} />
                </div>
                <div className="pgm-card__meta">
                  <span className={`chip ${tone === 'slate' ? 'outline' : tone} sm`}>
                    {formatFreshnessLabel(p.status || p.health?.overall || 'Tracking')}
                  </span>
                  {p.portfolioId ? (
                    <span className="pgm-card__portfolio">{p.portfolioId}</span>
                  ) : null}
                </div>
                {p.description ? <p className="pgm-card__desc">{p.description}</p> : null}
                <div className="pgm-card__foot">
                  <span className="pgm-card__foot-label">Program manager</span>
                  <span className="pgm-card__foot-value">{p.programManagerUserId || '—'}</span>
                </div>
              </Link>
            );
          })}
        </StaggerReveal>
      </section>

      <PageViewerControls
        page={r.data?.page}
        label="programs"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </div>
  );
}

function ProgramProjectsPanel({
  projectPager,
  currentWorkspaceTarget,
  currentWorkspaceLabel,
  className = 'span-12',
}) {
  return (
    <div className={`${className} card`}>
      <h2 className="card-title">Projects</h2>
      {projectPager.items.length === 0 ? (
        <EmptyState
          title="No projects"
          description="Child projects will appear here once they roll up into this program."
        />
      ) : (
        <ul className="pgm-rank-list">
          {projectPager.items.map((project) => {
            const progress = toNum(project.progressPercent);
            const tone = statusTone(project.status);
            return (
              <li key={project.id} className="pgm-rank-row">
                <div className="pgm-rank-row__main">
                  <Link
                    className="pgm-rank-row__name"
                    to={appendWorkspaceReturnContext(
                      project.route,
                      currentWorkspaceTarget,
                      currentWorkspaceLabel,
                    )}
                  >
                    {project.name}
                  </Link>
                  <div className="pgm-rank-row__status">
                    <HealthIndicator health={project.status} />
                  </div>
                  <ConfiguredFieldHighlights highlights={project.configuredFieldHighlights} />
                </div>
                <div className="pgm-rank-row__score">
                  <span className="pgm-rank-row__score-cap">Progress</span>
                  <InlineScoreCell
                    value={progress}
                    suffix="%"
                    tone={tone === 'slate' ? undefined : tone}
                    ariaLabel={`${project.name} progress ${progress}%`}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <PageViewerControls
        page={projectPager.page}
        label="projects"
        pageSize={projectPager.pageSize}
        onOffsetChange={projectPager.setOffset}
        onPageSizeChange={projectPager.setPageSize}
      />
    </div>
  );
}

function ProgramPlannerPanel({ planner, className = 'span-12' }) {
  return (
    <div className={`${className} card`}>
      <h2 className="card-title">Program coordination</h2>
      {(planner?.blockedActions || []).length === 0 ? (
        <EmptyState
          title="No priority actions"
          description="Blocked or due actions will appear here."
        />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Project</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            {planner.blockedActions.map((action) => (
              <tr key={action.id}>
                <td>
                  <Link to={action.route}>{action.title}</Link>
                </td>
                <td>{action.projectName}</td>
                <td>{action.priority}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h3 className="card-title" style={{ marginTop: 16 }}>
        Dependency pressure
      </h3>
      <div className="muted">
        {(planner?.dependencyRows || []).map((row) => (
          <div key={row.projectId}>
            {row.projectName}: {row.openDependencies} open / {row.blockedDependencies} blocked
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgramCapacityPanel({ resources, className = 'span-12' }) {
  const constrained = toNum(resources?.constrainedProjectCount);
  return (
    <div className={`${className} card`}>
      <h2 className="card-title">Resource capacity</h2>
      <div className="pgm-stat-strip">
        <div className="pgm-stat">
          <span className="pgm-stat__value">{resources?.activeAllocationCount ?? 0}</span>
          <span className="pgm-stat__label">active allocations</span>
        </div>
        <div className="pgm-stat">
          <span className="pgm-stat__value">{resources?.totalHoursPerWeek ?? 0}h / week</span>
          <span className="pgm-stat__label">team load</span>
        </div>
        <div className="pgm-stat">
          <span className={`pgm-stat__value ${constrained > 0 ? 'is-amber' : 'is-sage'}`}>
            {constrained}
          </span>
          <span className="pgm-stat__label">constrained projects</span>
        </div>
      </div>
      <h3 className="card-title" style={{ marginTop: 16 }}>
        Teams
      </h3>
      <div className="muted">
        {(resources?.teamRows || []).length === 0 ? (
          <div>No scoped team allocations available.</div>
        ) : (
          resources.teamRows.map((row) => (
            <div key={row.teamId}>
              {row.teamName}: {row.allocationHours}h / week
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ProgramBudgetPanel({
  financials,
  focusProjectId,
  currentWorkspaceTarget,
  currentWorkspaceLabel,
  className = 'span-12',
}) {
  const financialWorkspaceTarget = appendWorkspaceReturnContext(
    focusProjectId ? `/financials?focus=${encodeURIComponent(focusProjectId)}` : '/financials',
    currentWorkspaceTarget,
    currentWorkspaceLabel,
  );
  const burn = calculateBurnRatePercent(financials);
  const burnTone = burn > 100 ? 'is-brick' : burn > 90 ? 'is-amber' : 'is-sage';

  return (
    <div className={`${className} card`}>
      <h2 className="card-title">Budget execution</h2>
      <div className="pgm-stat-strip">
        <div className="pgm-stat">
          <span className="pgm-stat__value">
            {formatFinancialSummaryAmountUsdM(financials?.plannedBudgetUsdM)}
          </span>
          <span className="pgm-stat__label">planned budget</span>
        </div>
        <div className="pgm-stat">
          <span className="pgm-stat__value">
            {formatFinancialSummaryAmountUsdM(financials?.obligatedUsdM)}
          </span>
          <span className="pgm-stat__label">obligated</span>
        </div>
        <div className="pgm-stat">
          <span className={`pgm-stat__value ${burnTone}`}>{burn}%</span>
          <span className="pgm-stat__label">burn rate</span>
        </div>
      </div>
      <div className="muted" style={{ marginTop: 12 }}>
        <div>Planned: {formatFinancialSummaryAmountUsdM(financials?.plannedBudgetUsdM)}</div>
        <div>Obligated: {formatFinancialSummaryAmountUsdM(financials?.obligatedUsdM)}</div>
        <div>Expended: {formatFinancialSummaryAmountUsdM(financials?.actualSpendUsdM)}</div>
        <div>
          Forecast at completion:{' '}
          {formatFinancialSummaryAmountUsdM(financials?.forecastAtCompletionUsdM)}
        </div>
        <div>Variance: {formatFinancialSummaryAmountUsdM(financials?.forecastVarianceUsdM)}</div>
        <div>Burn rate: {burn}%</div>
        <div>Source freshness: {formatFreshnessLabel(financials?.sourceFreshness)}</div>
      </div>
      <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <Link className="btn ghost sm" to={financialWorkspaceTarget}>
          Open financial workspace
        </Link>
      </div>
    </div>
  );
}

function ProgramRelatedEvidencePanel({
  relatedEvidence,
  currentWorkspaceTarget,
  currentWorkspaceLabel,
  className = 'span-12',
}) {
  const counts = relatedEvidence?.data?.data?.counts;
  const actions = relatedEvidence?.data?.data?.actions || [];
  const artifacts = relatedEvidence?.data?.data?.artifacts || [];
  const comments = relatedEvidence?.data?.data?.comments || [];
  const auditSummary = relatedEvidence?.data?.data?.auditSummary;
  const previews = relatedEvidence?.data?.data?.previews || {};

  return (
    <div className={`${className} card`}>
      <h2 className="card-title">Related evidence</h2>
      {relatedEvidence?.loading ? (
        <LoadingSkeleton rows={3} />
      ) : relatedEvidence?.error ? (
        <ErrorState error={relatedEvidence.error} />
      ) : !counts ? (
        <EmptyState
          title="No related evidence"
          description="Linked actions, artifacts, comments, AI runs, and audit posture will appear here."
        />
      ) : (
        <>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <span className="chip blue">{counts.actions} actions</span>
            <span className="chip blue">{counts.risks} risks</span>
            <span className="chip blue">{counts.artifacts} artifacts</span>
            <span className="chip blue">{counts.aiRuns} AI runs</span>
            <span className="chip blue">{counts.briefings} briefings</span>
            <span className="chip blue">{counts.comments} comments</span>
            <span className="chip outline">{counts.auditEvents} audit events</span>
          </div>
          <div
            className="grid-auto"
            style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
          >
            <RelatedEvidencePreview
              family="actions"
              label="Actions"
              emptyLabel="No linked actions in scope."
              initialItems={actions}
              preview={previews.actions}
              renderItem={(action) => (
                <div key={action.id}>
                  <Link
                    to={appendWorkspaceReturnContext(
                      action.route,
                      currentWorkspaceTarget,
                      currentWorkspaceLabel,
                    )}
                  >
                    {action.title}
                  </Link>
                </div>
              )}
            />
            <RelatedEvidencePreview
              family="artifacts"
              label="Artifacts"
              emptyLabel="No linked artifacts in scope."
              initialItems={artifacts}
              preview={previews.artifacts}
              renderItem={(artifact) => (
                <div key={artifact.id}>
                  <Link
                    to={appendWorkspaceReturnContext(
                      artifact.route,
                      currentWorkspaceTarget,
                      currentWorkspaceLabel,
                    )}
                  >
                    {artifact.title}
                  </Link>
                </div>
              )}
            />
            <div>
              <div className="muted" style={{ marginBottom: 8 }}>
                {auditSummary?.totalEvents ?? 0} audit events
              </div>
              <RelatedEvidencePreview
                family="comments"
                label="Collaboration"
                emptyLabel="No linked comments in scope."
                initialItems={comments}
                preview={previews.comments}
                renderItem={(comment) => (
                  <div key={comment.id} style={{ marginTop: 8 }}>
                    <Link
                      to={appendWorkspaceReturnContext(
                        comment.route,
                        currentWorkspaceTarget,
                        currentWorkspaceLabel,
                      )}
                    >
                      {comment.excerpt}
                    </Link>
                  </div>
                )}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function ProgramProfilePage() {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectPaging = useUrlPagination(searchParams, setSearchParams, {
    defaultPageSize: 10,
    pageSizeParam: 'projectPageSize',
    offsetParam: 'projectOffset',
  });
  const p = useApi(`/programs/${id}`);
  const dashboard = useApi(`/programs/${id}/dashboard`);
  const projectPage = useApi(`/programs/${id}/projects`, {
    query: { limit: projectPaging.pageSize, offset: projectPaging.offset },
  });
  const relatedEvidence = useApi(`/programs/${id}/related`);
  const aiRuns = useApi('/ai/runs', { query: { scopeType: 'program', scopeId: id, limit: 20 } });
  const briefings = useApi('/briefings', {
    query: { scopeType: 'program', scopeId: id, limit: 20 },
  });
  const statusPacks = useApi('/status-packs', {
    query: { scopeType: 'program', scopeId: id, limit: 20 },
  });
  const data = p.data?.data;
  const dashboardData = dashboard.data?.data;
  const tab = getProgramWorkspaceTabFromSearchParams(searchParams);
  const projectPager = {
    items: projectPage.data?.data?.items || [],
    page: projectPage.data?.data?.page || {
      limit: projectPaging.pageSize,
      offset: projectPaging.offset,
      total: 0,
    },
    pageSize: projectPaging.pageSize,
    setOffset: projectPaging.setOffset,
    setPageSize: projectPaging.setPageSize,
  };
  const aiRunsList = aiRuns.data?.data || [];
  const briefingsList = briefings.data?.data || [];
  const statusPacksList = statusPacks.data?.data || [];
  const currentWorkspaceTarget = buildWorkspaceReturnTarget(location);
  const currentWorkspaceLabel = data?.name || 'Program workspace';
  const selectTab = (nextTab) => {
    setSearchParams(buildProgramWorkspaceTabSearchParams(searchParams, nextTab), {
      replace: false,
    });
  };

  if (p.loading || dashboard.loading || projectPage.loading)
    return (
      <div className="pgm-redesign">
        <PageHeader title="Program" />
        <KpiBandSkeleton count={4} />
        <div style={{ marginTop: 16 }}>
          <LoadingSkeleton rows={5} />
        </div>
      </div>
    );
  if (p.error || dashboard.error || projectPage.error)
    return <ErrorState error={p.error || dashboard.error || projectPage.error} />;
  if (!data) return <EmptyState title="Program not found" />;
  const breadcrumbs = prependWorkspaceBreadcrumb(
    [{ to: '/programs', label: 'Programs' }],
    searchParams,
  );
  if (dashboardData?.program?.portfolioRoute && dashboardData?.program?.portfolioName) {
    breadcrumbs.push({
      to: dashboardData.program.portfolioRoute,
      label: dashboardData.program.portfolioName,
    });
  }
  breadcrumbs.push({ label: data.name });

  const kpis = dashboardData?.kpis || {};
  const financials = dashboardData?.financials || {};
  const activeProjects = toNum(kpis.activeProjects);
  const atRiskProjects = toNum(kpis.atRiskProjects);
  const onTrackProjects = Math.max(0, activeProjects - atRiskProjects);
  const burnRatePercent = calculateBurnRatePercent(financials);
  const hasFinancials = financials.plannedBudgetUsdM != null;
  const lifecyclePhase = inferProgramLifecyclePhase(data, kpis);
  const lifecycleSteps = buildPipelineSteps({
    order: PROGRAM_LIFECYCLE_ORDER,
    current: lifecyclePhase,
    labels: PROGRAM_LIFECYCLE_LABELS,
  });
  const deliverySegments = [
    { label: 'On track', count: onTrackProjects, tone: 'sage' },
    { label: 'At risk', count: atRiskProjects, tone: atRiskProjects > 0 ? 'brick' : 'slate' },
  ];
  const heroItems = [
    {
      id: 'active',
      label: 'Active projects',
      value: kpis.activeProjects ?? 0,
      status: 'blue',
      statusLabel: 'In delivery',
    },
    {
      id: 'at-risk',
      label: 'At-risk projects',
      value: kpis.atRiskProjects ?? 0,
      status: atRiskProjects > 0 ? 'brick' : 'sage',
      statusLabel: atRiskProjects > 0 ? 'Attention' : 'Clear',
    },
    {
      id: 'open-actions',
      label: 'Open actions',
      value: kpis.openActions ?? 0,
      status: toNum(kpis.openActions) > 0 ? 'amber' : 'sage',
    },
    hasFinancials
      ? {
          id: 'burn',
          label: 'Burn rate',
          value: burnRatePercent,
          unit: '%',
          status: burnRatePercent > 100 ? 'brick' : burnRatePercent > 90 ? 'amber' : 'sage',
          statusLabel:
            burnRatePercent > 100 ? 'Overrun' : burnRatePercent > 90 ? 'Watch' : 'On plan',
          footer: `${formatFinancialSummaryAmountUsdM(financials.plannedBudgetUsdM)} planned`,
        }
      : {
          id: 'open-risks',
          label: 'Open risks',
          value: kpis.openRisks ?? 0,
          status: toNum(kpis.openRisks) > 0 ? 'amber' : 'sage',
        },
  ];

  return (
    <div className="pgm-redesign">
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="Program"
        title={data.name}
        subtitle={data.description}
        actions={
          <Link
            className="btn secondary sm"
            to={buildEnhancementReportPath({ scopeType: 'program', scopeId: data.id })}
          >
            Draft enhancement report
          </Link>
        }
      >
        <HealthIndicator health={data.health?.overall || data.status} />
      </PageHeader>
      <section className="bento">
        <div className="span-12 card profile-tabs-card">
          <div
            className="row"
            role="tablist"
            aria-label="Program workspace tabs"
            style={{ gap: 8, flexWrap: 'wrap' }}
          >
            {PROGRAM_WORKSPACE_TABS.map((workspaceTab) => (
              <button
                key={workspaceTab.key}
                role="tab"
                aria-selected={tab === workspaceTab.key}
                className={`btn ${tab === workspaceTab.key ? 'primary' : 'ghost'} sm`}
                onClick={() => selectTab(workspaceTab.key)}
              >
                {workspaceTab.label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'overview' && (
          <>
            <div className="span-12">
              <KpiHeroBand
                eyebrow="Program posture"
                context={
                  financials.sourceFreshness
                    ? formatFreshnessLabel(financials.sourceFreshness)
                    : undefined
                }
                columns={4}
                items={heroItems}
              />
            </div>

            <div className="span-8 card">
              <h2 className="card-title">Program lifecycle</h2>
              <div className="pgm-pipeline-wrap">
                <WorkflowPipeline steps={lifecycleSteps} ariaLabel="Program lifecycle" />
              </div>
              <p className="pgm-pipeline-caption">
                Current phase: {PROGRAM_LIFECYCLE_LABELS[lifecyclePhase] || lifecyclePhase} ·{' '}
                {activeProjects} active / {atRiskProjects} at risk
              </p>
            </div>

            <div className="span-4 card pgm-health-card">
              <h2 className="card-title">Delivery health</h2>
              {activeProjects + atRiskProjects > 0 ? (
                <StatusDonutMini
                  segments={deliverySegments}
                  size={104}
                  totalLabel="Projects"
                  centerValue={activeProjects || onTrackProjects + atRiskProjects}
                  ariaLabel="Program delivery health"
                />
              ) : (
                <span className="muted" style={{ fontSize: '0.82rem' }}>
                  No delivery data
                </span>
              )}
            </div>

            {/* FS-P3M-2: the legacy Analytics stub content merges into
                Overview instead of holding a dedicated tab. */}
            <div className="span-4 card">
              <h2 className="card-title">Status posture</h2>
              <div className="muted">
                <div>{dashboardData?.kpis?.activeProjects ?? 0} active projects</div>
                <div>{dashboardData?.kpis?.atRiskProjects ?? 0} at-risk projects</div>
                <div>{dashboardData?.kpis?.openRisks ?? 0} open risks</div>
              </div>
            </div>
            <div className="span-4 card">
              <h2 className="card-title">Delivery velocity</h2>
              <ul className="pgm-rank-list">
                {projectPager.items.map((project) => {
                  const progress = toNum(project.progressPercent);
                  const tone = statusTone(project.status);
                  return (
                    <li key={project.id} className="pgm-rank-row">
                      <div className="pgm-rank-row__main">
                        <span className="pgm-rank-row__name">{project.name}</span>
                      </div>
                      <div className="pgm-rank-row__score">
                        <span className="pgm-rank-row__score-cap">{progress}% progress</span>
                        <InlineScoreCell
                          value={progress}
                          suffix="%"
                          tone={tone === 'slate' ? undefined : tone}
                          ariaLabel={`${project.name} ${progress}% progress`}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="span-4 card">
              <h2 className="card-title">Decision posture</h2>
              <div className="muted">
                <div>{dashboardData?.kpis?.openActions ?? 0} actions pending</div>
                <div>
                  {dashboardData?.planner?.dependencyRows?.length ?? 0} dependency lanes tracked
                </div>
                <div>
                  {dashboardData?.resources?.constrainedProjectCount ?? 0} constrained projects
                </div>
              </div>
            </div>
            <ProgramProjectsPanel
              projectPager={projectPager}
              currentWorkspaceTarget={currentWorkspaceTarget}
              currentWorkspaceLabel={currentWorkspaceLabel}
            />
            <ProgramRelatedEvidencePanel
              relatedEvidence={relatedEvidence}
              currentWorkspaceTarget={currentWorkspaceTarget}
              currentWorkspaceLabel={currentWorkspaceLabel}
            />
          </>
        )}

        {tab === 'delivery' && (
          <>
            <ProgramPlannerPanel planner={dashboardData?.planner} className="span-7" />
            <ProgramCapacityPanel resources={dashboardData?.resources} className="span-5" />
            <ProgramProjectsPanel
              projectPager={projectPager}
              currentWorkspaceTarget={currentWorkspaceTarget}
              currentWorkspaceLabel={currentWorkspaceLabel}
            />
            <div className="span-12 card">
              <h2 className="card-title">Project builder</h2>
              <div
                className="row"
                style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
              >
                <p className="muted" style={{ margin: 0 }}>
                  Launch intake-approved work into the factory while staying in program context.
                </p>
                <Link className="btn primary sm" to="/project-factory">
                  Open Project Factory
                </Link>
              </div>
              <div className="muted" style={{ marginTop: 16 }}>
                {projectPager.items.map((project) => (
                  <div key={project.id}>
                    {project.name}: {project.progressPercent}% progress
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {tab === 'money' && (
          <ProgramBudgetPanel
            financials={dashboardData?.financials}
            focusProjectId={dashboardData?.projectRollup?.[0]?.id}
            currentWorkspaceTarget={currentWorkspaceTarget}
            currentWorkspaceLabel={currentWorkspaceLabel}
          />
        )}

        {tab === 'decisions' && (
          <>
            <div className="span-4 card">
              <h2 className="card-title">Decision posture</h2>
              <div className="muted">
                <div>{dashboardData?.kpis?.openActions ?? 0} actions pending</div>
                <div>
                  {dashboardData?.planner?.dependencyRows?.length ?? 0} dependency lanes tracked
                </div>
                <div>
                  {dashboardData?.resources?.constrainedProjectCount ?? 0} constrained projects
                </div>
              </div>
            </div>
            <div className="span-8 card">
              <h2 className="card-title">Roadmap sequencing</h2>
              {(dashboardData?.planner?.dependencyRows || []).length === 0 ? (
                <EmptyState
                  title="No roadmap sequencing"
                  description="Program roadmap sequencing will appear when child projects carry dependency data."
                />
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Open dependencies</th>
                      <th>Blocked dependencies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.planner.dependencyRows.map((row) => (
                      <tr key={row.projectId}>
                        <td>{row.projectName}</td>
                        <td>{row.openDependencies}</td>
                        <td>{row.blockedDependencies}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === 'risks' && (
          <>
            <div className="span-4 card">
              <h2 className="card-title">Risk posture</h2>
              <div className="muted">
                <div>{dashboardData?.kpis?.openRisks ?? 0} open risks</div>
                <div>{dashboardData?.kpis?.atRiskProjects ?? 0} at-risk projects</div>
              </div>
            </div>
            <div className="span-8 card">
              <h2 className="card-title">Action pressure</h2>
              {(dashboardData?.planner?.blockedActions || []).length === 0 ? (
                <EmptyState
                  title="No blocked actions"
                  description="Blocked actions will appear here when the program needs intervention."
                />
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Project</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.planner.blockedActions.map((action) => (
                      <tr key={action.id}>
                        <td>
                          <Link to={action.route}>{action.title}</Link>
                        </td>
                        <td>{action.projectName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === 'evidence' && (
          <div className="span-12 card">
            <h2 className="card-title">AI review posture</h2>
            {aiRuns.loading ? (
              <LoadingSkeleton rows={3} />
            ) : aiRuns.error ? (
              <ErrorState error={aiRuns.error} />
            ) : aiRunsList.length === 0 ? (
              <EmptyState
                title="No AI runs"
                description="Program-scoped AI reviews will appear here when governed runs are created."
              />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Status</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {aiRunsList.map((run) => (
                    <tr key={run.id}>
                      <td>
                        <Link to={`/ai/runs/${run.id}`}>{run.name || run.id}</Link>
                      </td>
                      <td>{run.reviewState || run.status || 'Pending review'}</td>
                      <td>{run.confidence ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'evidence' && (
          <div className="span-12 card">
            <div
              className="row"
              style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
            >
              <h2 className="card-title" style={{ margin: 0 }}>
                Status products
              </h2>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <Link
                  className="btn ghost sm"
                  to={buildExecutiveDeckPath({ scopeType: 'program', scopeId: id })}
                >
                  Draft executive deck
                </Link>
                <Link
                  className="btn secondary sm"
                  to={buildEnhancementReportPath({ scopeType: 'program', scopeId: id })}
                >
                  Draft enhancement report
                </Link>
                <Link
                  className="btn outline sm"
                  to={buildStatusPackLibraryPath({
                    scopeType: 'program',
                    scopeId: id,
                    scopeLabel: data?.name || 'Program workspace',
                    title: `${data?.name || 'Program'} stakeholder status`,
                  })}
                >
                  Draft status pack
                </Link>
              </div>
            </div>
            {briefings.loading ? (
              <LoadingSkeleton rows={3} />
            ) : briefings.error ? (
              <ErrorState error={briefings.error} />
            ) : briefingsList.length === 0 ? (
              <EmptyState
                title="No program status products"
                description="Generated program decks and reports will appear here."
              />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {briefingsList.map((brief) => (
                    <tr key={brief.id}>
                      <td>
                        <Link to={`/briefings/${brief.id}`}>
                          {brief.title || brief.name || brief.id}
                        </Link>
                      </td>
                      <td>{brief.status || 'Draft'}</td>
                      <td>{brief.updatedAt || brief.generatedAt || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <h3 className="card-title" style={{ marginTop: 16 }}>
              Stakeholder status packs
            </h3>
            {statusPacks.loading ? (
              <LoadingSkeleton rows={2} />
            ) : statusPacks.error ? (
              <ErrorState error={statusPacks.error} />
            ) : statusPacksList.length === 0 ? (
              <EmptyState
                title="No status packs"
                description="Program-scoped stakeholder packs will appear here after draft creation and publishing."
              />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Status pack</th>
                    <th>Status</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {statusPacksList.map((pack) => (
                    <tr key={pack.id}>
                      <td>
                        <Link to={`/status-packs/${pack.id}`}>{pack.title || pack.id}</Link>
                      </td>
                      <td>{pack.status || 'Draft'}</td>
                      <td>{pack.shareId || 'Not published'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
